import React, { useState } from 'react';
import { Phone, PhoneOff, User, Clock, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useTwilioVoice } from '@/hooks/useTwilioVoice';
import { cn } from '@/lib/utils';

interface OutboundCallDialerProps {
  className?: string;
  onCallStarted?: (callSid: string) => void;
  onCallEnded?: () => void;
}

export function OutboundCallDialer({ 
  className, 
  onCallStarted, 
  onCallEnded 
}: OutboundCallDialerProps) {
  const [phoneNumber, setPhoneNumber] = useState('');
  const { callState, isLoading, makeCall, endCall, resetCallState } = useTwilioVoice();

  const handleMakeCall = async () => {
    if (!phoneNumber.trim()) return;
    
    const success = await makeCall({ to: phoneNumber });
    if (success && callState.callSid) {
      onCallStarted?.(callState.callSid);
    }
  };

  const handleEndCall = async () => {
    const success = await endCall();
    if (success) {
      onCallEnded?.();
      resetCallState();
    }
  };

  const formatPhoneNumber = (value: string) => {
    // Format as user types
    const cleaned = value.replace(/\D/g, '');
    let formatted = cleaned;
    
    if (cleaned.length >= 3 && cleaned.length < 6) {
      formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    } else if (cleaned.length >= 6) {
      formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    }
    
    return formatted;
  };

  const getStatusBadge = () => {
    const statusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
      'idle': { label: 'Prêt', variant: 'outline' },
      'initiating': { label: 'Connexion...', variant: 'secondary' },
      'ringing': { label: 'Sonnerie', variant: 'default' },
      'in-progress': { label: 'En cours', variant: 'default' },
      'completed': { label: 'Terminé', variant: 'secondary' },
      'failed': { label: 'Échec', variant: 'destructive' },
      'busy': { label: 'Occupé', variant: 'destructive' },
      'no-answer': { label: 'Pas de réponse', variant: 'destructive' },
    };

    const config = statusConfig[callState.status] || statusConfig.idle;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const isCallActive = ['ringing', 'in-progress', 'initiating'].includes(callState.status);

  return (
    <Card className={cn("w-full", className)}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-lg">
          <div className="flex items-center gap-2">
            <Phone className="h-5 w-5 text-primary" />
            Appels sortants
          </div>
          {getStatusBadge()}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!isCallActive ? (
          <>
            <div className="space-y-2">
              <Label htmlFor="phone-number">Numéro de téléphone</Label>
              <Input
                id="phone-number"
                type="tel"
                placeholder="(514) 555-1234"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
                disabled={isLoading}
              />
            </div>

            {/* Quick dial pad */}
            <div className="grid grid-cols-3 gap-2">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map((digit) => (
                <Button
                  key={digit}
                  variant="outline"
                  size="sm"
                  onClick={() => setPhoneNumber(prev => prev + digit)}
                  disabled={isLoading}
                  className="h-10 text-lg font-semibold"
                >
                  {digit}
                </Button>
              ))}
            </div>

            <Button
              onClick={handleMakeCall}
              disabled={!phoneNumber.trim() || isLoading}
              className="w-full bg-green-600 hover:bg-green-700"
              size="lg"
            >
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
              ) : (
                <Phone className="h-5 w-5 mr-2" />
              )}
              Appeler
            </Button>
          </>
        ) : (
          <div className="space-y-4">
            {/* Active call display */}
            <div className="bg-muted/50 rounded-lg p-4 text-center space-y-2">
              <div className="flex items-center justify-center gap-2 text-muted-foreground">
                <User className="h-4 w-4" />
                <span className="font-medium">{callState.to || phoneNumber}</span>
              </div>
              
              {callState.status === 'ringing' && (
                <div className="flex items-center justify-center gap-2 animate-pulse">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-ping" />
                  <span className="text-sm text-muted-foreground">Sonnerie en cours...</span>
                </div>
              )}
              
              {callState.status === 'in-progress' && (
                <div className="flex items-center justify-center gap-2 text-green-600">
                  <Clock className="h-4 w-4" />
                  <span className="font-mono">
                    {callState.duration ? `${Math.floor(callState.duration / 60)}:${(callState.duration % 60).toString().padStart(2, '0')}` : '0:00'}
                  </span>
                </div>
              )}
            </div>

            <Button
              onClick={handleEndCall}
              disabled={isLoading}
              variant="destructive"
              className="w-full"
              size="lg"
            >
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
              ) : (
                <PhoneOff className="h-5 w-5 mr-2" />
              )}
              Raccrocher
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
